import clsx from 'clsx'
import React, { useEffect } from 'react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Link } from '@wartek-id/link'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'

import ProfileHeader from 'app/PemetaanKompetensiPage/components/ProfileRevamp/ProfileHeader'
import CompetencyIndicatorGroup from 'app/PemetaanKompetensiPage/components/RPD/CompetencyIndicatorGroup'
import CompetencyIndicatorCheckbox from 'app/PemetaanKompetensiPage/components/RPD/CompetencyIndicatorCheckbox'
import { CompetencyIcons } from 'app/PemetaanKompetensiPage/constants/CompetencyIcons'
import CenterDialog from 'app/PemetaanKompetensiPage/components/CenterDialog'
import AddReasonSheet from 'app/PemetaanKompetensiPage/components/RPD/AddReasonSheet'
import {
  useAPISelfImprovementContentCollection,
  useAPISelfImprovementPlan,
  useMutateAPISelfImprovementPlan,
} from 'api/self-improvement/useAPISelfImprovement'

import type { IIndicatorDetail } from 'types/self-improvement'
import type { RequestBodyPlan } from 'api/self-improvement/useAPISelfImprovement'
import type { FC } from 'react'

interface MainProps {}
export interface IInputIndicator extends IIndicatorDetail {
  reason: string
  needs?: string
}

const Main: FC<MainProps> = ({}) => {
  const [showDraftDialog, setShowDraftDialog] = React.useState(false)
  const [showAddReasonSheet, setShowAddReasonSheet] =
    React.useState<IInputIndicator | null>(null)
  const [indicatorList, setIndicatorList] = React.useState<IInputIndicator[]>(
    []
  )

  const router = useRouter()

  const { data: indicatorData, isLoading: isLoadingIndicatorData } =
    useAPISelfImprovementContentCollection('RPD', 'INDICATOR')

  const { data: planData, isLoading: isLoadingPlan } =
    useAPISelfImprovementPlan()

  const mutateAPISelfImprovementPlan = useMutateAPISelfImprovementPlan()

  const toast = useToast()

  useEffect(() => {
    if (!isLoadingPlan && planData) {
      const savedIndicatorList = planData.data.indicators.map(
        (thisIndicatorData) => ({
          indicator_key: thisIndicatorData.key,
          needs: thisIndicatorData.needs,
          reason: thisIndicatorData.reason,
        })
      )
      setIndicatorList(savedIndicatorList)
    }
  }, [planData, isLoadingPlan])

  const handleUpdateIndicatorReason = (data: IInputIndicator) => {
    let selectedIndicatorIndex = -1
    if (indicatorList.length) {
      selectedIndicatorIndex = indicatorList.findIndex(
        (thisIndicator) => thisIndicator.indicator_key == data.indicator_key
      )
    }

    let indicatorChanged = []

    indicatorChanged = [...indicatorList]

    if (selectedIndicatorIndex > -1) {
      indicatorChanged.splice(selectedIndicatorIndex, 1)
    }

    indicatorChanged.push(data)
    setIndicatorList(indicatorChanged)
  }

  const handleCheckboxIndicator = (data: IInputIndicator) => {
    let selectedIndicatorIndex = -1
    if (indicatorList.length) {
      selectedIndicatorIndex = indicatorList.findIndex(
        (thisIndicator) => thisIndicator.indicator_key == data.indicator_key
      )
    }

    let indicatorChanged = []

    if (selectedIndicatorIndex > -1) {
      indicatorChanged = [...indicatorList]
      indicatorChanged.splice(selectedIndicatorIndex, 1)
    } else {
      indicatorChanged = [...indicatorList, data]
    }

    setIndicatorList(indicatorChanged)
  }

  const handleSubmit = (action: 'exit' | 'next') => {
    if (!indicatorList.length) {
      toast({
        message: 'Anda harus pilih minimal 1 indikator',
      })
      return
    }

    const noReason = indicatorList.find(
      (thisIndicator) => !thisIndicator.reason
    )

    if (noReason) {
      // TODO: error message unconfirmed
      toast({
        message: 'Anda harus sertakan alasan indikator',
      })
      return
    }

    const payload: RequestBodyPlan = {
      state: 'DRAFTED',
      indicators: indicatorList.map((thisIndicatorInput) => ({
        key: thisIndicatorInput.indicator_key,
        reason: thisIndicatorInput.reason,
        needs: thisIndicatorInput.needs,
      })),
    }

    mutateAPISelfImprovementPlan.mutate(payload, {
      onSuccess: () => {
        // TODO handle success here
        toast({
          message: 'Berhasil simpan RPD!',
        })
        if (action === 'exit') {
          router.back()
        }
      },
    })
  }

  return (
    <div className="pb-6">
      <ProfileHeader
        onClickBack={() => setShowDraftDialog(true)}
        classNameSubContainer={clsx(
          'bg-cover',
          'bg-top',
          "bg-[url('/images/header-rencana-pengembangan-diri-min.png')]",
          'flex',
          'items-start',
          'justify-between',
          'flex-col'
        )}
        children={
          <div className="pl-4 pr-6 w-full mt-[50px] space-y-1 mb-4 flex flex-col">
            <Text color="inverse" variant="heading-lg">
              Rencana Pengembangan Diri (RPD)
            </Text>
            <div className="inline-flex space-x-1">
              <Text color="inverse">Batas waktu:</Text>
              <Text color="inverse" variant="body-bold">
                31 Januari 2024.
              </Text>
            </div>
            <Link variant="white" className="!p-0">
              Lihat riwayat RPD
            </Link>
          </div>
        }
      />
      <div className="bg-blue-5 p-4 space-y-2">
        <Text variant="heading-md">Panduan</Text>
        <ol className="list-disc px-4">
          <Text as="li" variant="body-sm">
            Pilih indikator untuk fokus pembelajaran satu tahun ke depan
          </Text>
          <Text as="li" variant="body-sm">
            Diskusikan dengan kepala sekolah untuk bimbingan selengkapnya
          </Text>
        </ol>
      </div>
      <form className="py-6 px-4">
        {!isLoadingIndicatorData &&
          !isLoadingPlan &&
          indicatorData?.data[0]?.children.map((thisIndicatorTeknis, index) => (
            <CompetencyIndicatorGroup
              key={`teknis_${index}`}
              name={thisIndicatorTeknis.name}
              icon={CompetencyIcons[thisIndicatorTeknis.name.toLowerCase()]}
            >
              {thisIndicatorTeknis.children.map(
                (thisIndicatorDetail, index) => (
                  <CompetencyIndicatorCheckbox
                    key={`${thisIndicatorTeknis.name}${index}`}
                    indicator_key={thisIndicatorDetail.key}
                    name={thisIndicatorDetail.content.title}
                    description={thisIndicatorDetail.content.description}
                    handleCheckboxIndicator={(data) =>
                      handleCheckboxIndicator(data)
                    }
                    inputIndicatorData={indicatorList.find(
                      (selectedIndicator) =>
                        selectedIndicator.indicator_key ==
                        thisIndicatorDetail.key
                    )}
                    handleAddReason={() => {
                      const selectedIndicator = indicatorList.find(
                        (thisIndicator) =>
                          thisIndicator.indicator_key == thisIndicatorDetail.key
                      )

                      setShowAddReasonSheet({
                        ...selectedIndicator,
                        content: thisIndicatorDetail.content,
                      })
                    }}
                  />
                )
              )}
            </CompetencyIndicatorGroup>
          ))}
      </form>
      <div className="px-4">
        <Button
          color="blue"
          fullWidth
          size="lg"
          onClick={() => handleSubmit('next')}
        >
          Lanjut
        </Button>
      </div>
      <CenterDialog
        isLoading={false}
        open={showDraftDialog}
        description="Pilihan yang anda isi akan disimpan dan Anda bisa lanjutkan nanti."
        CTATextSecondary="Kembali"
        handleClose={() => setShowDraftDialog(false)}
        handleCTASecondary={() => setShowDraftDialog(false)}
        handleCTA={() => {
          setShowDraftDialog(false)
          handleSubmit('exit')
        }}
      />
      <AddReasonSheet
        isLoading={false}
        open={!!showAddReasonSheet}
        dialogData={showAddReasonSheet}
        handleClose={() => setShowAddReasonSheet(null)}
        handleSave={(data) => {
          setShowAddReasonSheet(null)
          handleUpdateIndicatorReason(data)
        }}
      />
    </div>
  )
}
export default Main
